import {
  Coffee,
  Sun,
  Moon,
  Apple,
  Plus,
  ChevronRight,
  Camera,
  Calendar,
  Info,
} from "lucide-react";
import welcomeBg from "@/projects/Admin_Bite_Balance/bite-balance/src/assets/welcome-bg.jpg";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Calendar as CalendarComponent } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useState } from "react";

const Meals = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );

  const meals = [
    {
      name: "Breakfast",
      icon: Coffee,
      time: "8:30 AM",
      items: ["Oatmeal with berries", "Greek yogurt", "Green tea"],
      calories: 420,
      protein: 18,
      carbs: 65,
      fat: 12,
      mealScore: 82,
      color: "bg-chart-3",
    },
    {
      name: "Lunch",
      icon: Sun,
      time: "1:00 PM",
      items: ["Grilled chicken salad", "Quinoa", "Olive oil dressing"],
      calories: 580,
      protein: 38,
      carbs: 45,
      fat: 22,
      mealScore: 91,
      color: "bg-chart-2",
    },
    {
      name: "Snack",
      icon: Apple,
      time: "4:30 PM",
      items: ["Apple with almond butter", "Mixed nuts"],
      calories: 220,
      protein: 7,
      carbs: 28,
      fat: 11,
      mealScore: 65,
      color: "bg-chart-4",
    },
    {
      name: "Dinner",
      icon: Moon,
      time: "7:30 PM",
      items: ["Baked salmon", "Sweet potato", "Steamed broccoli"],
      calories: 520,
      protein: 35,
      carbs: 42,
      fat: 18,
      mealScore: 88,
      color: "bg-chart-1",
    },
  ];

  const totalCalories = meals.reduce((sum, meal) => sum + meal.calories, 0);
  const totalProtein = meals.reduce((sum, meal) => sum + meal.protein, 0);
  const totalCarbs = meals.reduce((sum, meal) => sum + meal.carbs, 0);
  const totalFat = meals.reduce((sum, meal) => sum + meal.fat, 0);
  const calorieGoal = 2000;
  const proteinGoal = 100;
  const carbsGoal = 200;
  const fatGoal = 65;
  const calorieProgress = (totalCalories / calorieGoal) * 100;

  return (
    <div className="relative min-h-screen overflow-hidden pb-24">
      {/* Blurred Background Image */}
      <div
        className="fixed inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />

      {/* Overlay for better contrast */}
      <div className="fixed inset-0 bg-background/70" />

      <div className="relative z-10 mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Today's Meals
            </h1>
            <p className="text-sm text-muted-foreground/70">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-xl border-border/50"
              >
                <Calendar className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="z-50 w-auto bg-card p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                initialFocus
                className="pointer-events-auto"
              />
              {selectedDate && (
                <div className="border-t border-border bg-card p-3">
                  <p className="text-center text-xs text-muted-foreground">
                    Viewing meals for {format(selectedDate, "PPP")}
                  </p>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Daily Summary Card with Circular Progress */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-primary/10 p-5 shadow-sm">
          <div className="mb-4">
            <h3 className="mb-1 text-sm font-bold text-black dark:text-white">
              Today's Intake
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {totalCalories}
              </span>
              <span className="text-sm text-muted-foreground">
                / {calorieGoal} kcal
              </span>
            </div>
          </div>

          {/* 2x2 Grid Layout for Macros */}
          <div className="grid grid-cols-2 gap-4">
            {/* Calories Percentage */}
            <div className="flex items-center gap-3 rounded-xl bg-background/50 p-3">
              <div className="relative h-12 w-12 flex-shrink-0">
                <svg className="h-12 w-12 -rotate-90 transform">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-muted/20"
                  />
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - Math.min(calorieProgress / 100, 1))}`}
                    className="text-chart-1 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-foreground">
                    {Math.round(calorieProgress)}%
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  Calories
                </span>
                <span className="text-base font-bold text-foreground">
                  {Math.round(calorieProgress)}%
                </span>
                <span className="text-[10px] text-muted-foreground">
                  of daily goal
                </span>
              </div>
            </div>

            {/* Carbs */}
            <div className="flex items-center gap-3 rounded-xl bg-background/50 p-3">
              <div className="relative h-12 w-12 flex-shrink-0">
                <svg className="h-12 w-12 -rotate-90 transform">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-muted/20"
                  />
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - totalCarbs / carbsGoal)}`}
                    className="text-chart-2 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-foreground">
                    {Math.round((totalCarbs / carbsGoal) * 100)}%
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  Carbs
                </span>
                <span className="text-base font-bold text-foreground">
                  {totalCarbs}g
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Goal: {carbsGoal}g
                </span>
              </div>
            </div>

            {/* Fat */}
            <div className="flex items-center gap-3 rounded-xl bg-background/50 p-3">
              <div className="relative h-12 w-12 flex-shrink-0">
                <svg className="h-12 w-12 -rotate-90 transform">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-muted/20"
                  />
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - totalFat / fatGoal)}`}
                    className="text-chart-3 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-foreground">
                    {Math.round((totalFat / fatGoal) * 100)}%
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  Fat
                </span>
                <span className="text-base font-bold text-foreground">
                  {totalFat}g
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Goal: {fatGoal}g
                </span>
              </div>
            </div>

            {/* Protein */}
            <div className="flex items-center gap-3 rounded-xl bg-background/50 p-3">
              <div className="relative h-12 w-12 flex-shrink-0">
                <svg className="h-12 w-12 -rotate-90 transform">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-muted/20"
                  />
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - totalProtein / proteinGoal)}`}
                    className="text-chart-4 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-foreground">
                    {Math.round((totalProtein / proteinGoal) * 100)}%
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-muted-foreground">
                  Protein
                </span>
                <span className="text-base font-bold text-foreground">
                  {totalProtein}g
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Goal: {proteinGoal}g
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 gap-2">
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-lg font-bold text-foreground">4</div>
            <div className="text-xs text-muted-foreground">Meals</div>
          </Card>
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-lg font-bold text-secondary">140g</div>
            <div className="text-xs text-muted-foreground">Net Carbs</div>
          </Card>
        </div>

        {/* Meals List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Meal Log</h3>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => navigate("/meals/history")}
            >
              View History <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {meals.map((meal, index) => {
            const Icon = meal.icon;
            const mealTypes = ["breakfast", "lunch", "snack", "dinner"];
            return (
              <Card
                key={index}
                className="cursor-pointer rounded-xl border-0 p-4 shadow-sm transition-shadow hover:shadow-md"
                onClick={() => navigate(`/meals/${mealTypes[index]}`)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`h-12 w-12 rounded-xl ${meal.color}/20 flex flex-shrink-0 items-center justify-center`}
                  >
                    <Icon
                      className={`h-6 w-6 ${meal.color.replace("bg-", "text-")}`}
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center justify-between">
                      <h4 className="font-semibold text-foreground">
                        {meal.name}
                      </h4>
                      <div className="flex items-center gap-2">
                        <div
                          className="flex cursor-pointer items-center gap-1 transition-opacity hover:opacity-80"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/meals/${mealTypes[index]}/score`);
                          }}
                        >
                          <span
                            className={`text-xs font-bold ${meal.mealScore >= 80 ? "text-chart-2" : meal.mealScore >= 60 ? "text-primary" : "text-destructive"}`}
                          >
                            {meal.mealScore}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            /100
                          </span>
                          <Info className="ml-0.5 h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                    <p className="mb-1 text-xs text-muted-foreground">
                      {meal.time}
                    </p>

                    <div className="space-y-0.5">
                      {meal.items.map((item, i) => (
                        <p key={i} className="text-sm text-muted-foreground">
                          • {item}
                        </p>
                      ))}
                    </div>
                  </div>

                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Meals;
